import React from 'react';
import './Terms.css';

const Terms = () => {
  return (
    <div className="terms-page">
      <section className="terms-hero">
        <div className="container">
          <h1>Terms &amp; Conditions</h1>
          <p className="last-updated">Last Updated: January 22, 2026</p>
        </div>
      </section>

      <div className="terms-content">
        <section className="terms-section">
          <h2>1. Company Information</h2>
          <p>
           These Terms and Conditions ("Terms") govern the sale of imported packaged snack products ("Products") 
           by <strong>MunchUps (NordicBloom AB)</strong>, a company registered in <strong>Stockholm, Sweden</strong>, with its registered address at 
           <strong>Näsbydalsvägen 6, Täby, 183 31, Stockholm Län, Sweden</strong> ("Company", "we", "us", or "our").
          </p>
        </section>

        <section className="terms-section">
          <h2>2. Scope Of Application</h2>
          <p>
            These Terms apply to all orders, purchases, and contracts between the Company and its customers ("Customer", "you") through our website, 
            marketplace listings, or other sales channels, unless otherwise agreed in writing.
          </p>
        </section>

        <section className="terms-section">
          <h2>3. Product Description &amp; Compliance</h2>
          <p>3.1 Our Products are imported from countries outside the European Union and are sold in sealed, pre-packaged form.</p>
          <p>3.2 We make reasonable efforts to ensure that Products comply with applicable EU food safety, labeling, and import regulations at the time of sale, 
            including but not limited to Regulation (EU) No 1169/2011 on food information to consumers.</p>
          <p>
            3.3 Customers acknowledge that:
            <ul>
              <li>Products may contain allergens or ingredients that are not common in the EU; it is the Customer's responsibility to review product information and ensure suitability for their dietary needs.</li>
              <li>Due to the nature of imported food products, there may be variations in packaging, labeling, or ingredient lists compared to similar products sold within the EU.</li>
              <li>We do not provide guarantees regarding the shelf life or storage conditions of Products once they are open and in the Customer's possession.</li>
              <li>Some Products may contain additives, colorings, or ingredients not commonly used in the EU.</li>
            </ul>
          </p>
          <p>
            3.4 Ingredient lists and allergen information are provided as supplied by the manufacturer and/or translated where required. Minor translation discrepancies may occur. 
            Customers with allergies or dietary restrictions should exercise caution and consult product information carefully before purchase.
          </p>
          <p>
            3.5 We reserve the right to refuse service, cancel orders, or limit quantities if we believe that a customer's actions violate these Terms or applicable laws.
          </p>
        </section>

        <section className="terms-section">
          <h2>4. Allergen &amp; Dietary Information</h2>
          <p>
            4.1 Products may contain or come into contact with allergens such as nuts, peanuts, soy, milk, eggs, gluten, sesame, and others.
          </p>
          <p>
            4.2 Customers are responsible for reviewing ingredient and allergen information prior to purchase. 
            If you have allergies or dietary restrictions, please contact us before ordering.
          </p>
          <p>
            4.3 We do not guarantee that Products are suitable for specific diets (including vegan, vegetarian, halal, or kosher) unless explicitly stated.
          </p>
        </section>

        <section className="terms-section">
          <h2>5. Pricing &amp; Payments</h2>
          <p>
            5.1 All prices are listed in Swedish Krona (SEK) and include applicable VAT unless otherwise stated.
          </p>
          <p>
            5.2 Shipping costs will be added to the order as per shipping policy. Shipping charges will be notified to the customer once order is received.
          </p>
          <p>
            5.3 Accepted payment methods are displayed on our website. Payment must be received in full before an order is processed and shipped.
          </p>
          <p>
            5.4 We reserve the right to correct pricing errors and to cancel orders affected by such errors.
          </p>
        </section>

        <section className="terms-section">
          <h2>6. Orders &amp; Confirmation</h2>
          <p>
            6.1 An order is considered binding once you receive an order confirmation email from MunchUps.
          </p>
          <p>
            6.2 If an item becomes unavailable after you place an order, we will notify you and issue a refund for the affected item.
          </p>
          <p>
            6.3 We reserve the right to reject or cancel orders suspected of fraud, misuse, or violation of these Terms.
          </p>
        </section>

        <section className="terms-section">
          <h2>7. Shipping &amp; Delivery</h2>
          <p>
            7.1 Delivery times are estimates only and may vary due to stock unavailability, 
            carrier delays, or other factors beyond our control. You will receive a notification 
            from us regarding the estimated delivery time for your order.
          </p>
          <p>
            7.2 Risk of loss or damage passes to the Customer upon delivery.
          </p>
          <p>
            7.3 Customers are responsible for ensuring that food products are permitted in their destination country.
          </p>
          <p>
            7.4 We deliver to addresses within Sweden unless otherwise stated.
          </p>
          <p>
            7.5 Customers are responsible for ensuring that delivery information is accurate. 
            MunchUps is not responsible for delays or failed deliveries caused by incorrect details.
          </p>
        </section>

        <section className="terms-section">
          <h2>8. Right of Withdrawal and Returns</h2>
          <p>
            8.1 Due to the nature of food products, returns and withdrawals are governed by applicable consumer protection laws.
          </p>
          <p>
            8.2 Customers have the right to withdraw from a purchase within 14 days of receiving the product, provided that the product is unopened, undamaged, and in its original packaging.
          </p>
          <p>
            8.3 To exercise the right of withdrawal, customers must contact us at <strong><a href="mailto:info.munchups@gmail.com">info.munchups@gmail.com</a></strong> with their order details and reason for withdrawal. We will provide instructions for returning the product.
          </p>
          <p>
            8.4 If Products are damaged, defective, or incorrect upon delivery, you must notify us within 48 hours of receipt, providing photographic evidence where possible.
          </p>
        </section>

        <section className="terms-section">
          <h2>9. Shelf Life &amp; Storage</h2>
          <p>
            9.1 Products are sold with a best-before or expiration date indicated on the packaging.
          </p>
          <p>
            9.2 Customers are responsible for proper storage of Products according to the instructions on the packaging.
          </p>
        </section>

        <section className="terms-section">
          <h2>10. Limitation of Liability</h2>
          <p>
            10.1 To the maximum extent permitted by law, we shall not be liable for:
            <ul>
              <li>Differences in taste or expectations.</li>
              <li>Adverse reactions due to undisclosed allergies or sensitivities.</li>
              <li>Delays or failures in delivery caused by carrier or due to factors beyond our control.</li>
            </ul>
          </p>
          <p>10.2 Nothing in these Terms limits liability for death or personal injury caused by gross negligence or 
            willful misconduct, or any liability that cannot be excluded under applicable law.</p>
        </section>

        <section className="terms-section">
          <h2>11. Intellectual Property</h2>
          <p>
            All trademarks, product names, images, and content on our website remain the property 
            of their respective owners and are used for identification purposes only.
          </p>
        </section>

        <section className="terms-section">
          <h2>12. Data Protection</h2>
          <p>
            Personal data is processed in accordance with applicable data protection laws, 
            including the General Data Protection Regulation (GDPR). Please refer to our Privacy Policy for details.
          </p>
        </section>

        <section className="terms-section">
          <h2>13. Governing Law and Jurisdiction</h2>
          <p>
            These Terms are governed by the laws of Sweden, without regard to conflict-of-law principles. 
            Any disputes shall be subject to the exclusive jurisdiction of the courts of Sweden.
          </p>
        </section>

        <section className="terms-section">
          <h2>14. Amendments</h2>
          <p>
            We reserve the right to amend these Terms at any time. 
            Updated Terms will be published on our website and apply to orders placed thereafter.
          </p>
        </section>

        <section className="terms-section">
          <h2>15. Contact Information</h2>
          <p>
            For questions or complaints, please contact us at:
          </p>
          <p>
            <strong>MunchUps (NordicBloom AB)</strong><br />
            <strong>Email: </strong><a href="mailto:info.munchups@gmail.com">info.munchups@gmail.com</a><br />
            <strong>Address:</strong> Näsbydalsvägen 6, Täby, 183 31, Stockholm Län, Sweden
          </p>
        </section>

        <div className="terms-footer">
          <p>
            By using our website, you acknowledge that you have read and understood this Terms &amp; Conditions 
            and agree to it.
          </p>
          <p>
            <strong>Last Updated: January 22, 2026</strong>
          </p>
        </div>

      </div>
    </div>
  );
};

export default Terms;
